<template>
    <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen py-10 px-4">
        <AppHeader />
        <!-- CONTAINER -->
        <div class="max-w-5xl mx-auto space-y-6">

            <!-- HEADER DO PROJETO -->
            <div class="bg-white shadow rounded-2xl p-6 flex flex-col gap-4">

                <div class="flex justify-between items-start flex-wrap gap-4">
                    <div>
                        <h1 class="text-3xl font-bold">
                            {{ project.title }}
                        </h1>
                        <p class="text-gray-500 text-sm mt-1">
                            Publicado em {{ formatDate(project.created_at) }}
                        </p>
                    </div>

                    <div class="flex flex-col items-start gap-2">
                        <span class="px-4 py-2 rounded-xl text-white text-sm font-semibold" :class="{
                            'bg-green-600': project.urgency_level === 'low',
                            'bg-yellow-500': project.urgency_level === 'medium',
                            'bg-red-600': project.urgency_level === 'high'
                        }">
                            {{ urgencyLabel(project.urgency_level) }}
                        </span>

                        <span class="px-3 py-1 text-xs font-semibold rounded-full" :class="{
                            'bg-blue-100 text-blue-700': project.status === 'open',
                            'bg-yellow-100 text-yellow-700': project.status === 'in_progress',
                            'bg-green-100 text-green-700': project.status === 'completed',
                            'bg-red-100 text-red-700': project.status === 'cancelled',
                            'bg-orange-100 text-orange-700': project.status === 'awaiting_approval',
                            'bg-purple-100 text-purple-700': project.status === 'awaiting_payment',
                            'bg-emerald-100 text-emerald-700': project.status === 'payment_approved',
                            'bg-amber-100 text-amber-700': project.status === 'cancel_requested',
                            'bg-fuchsia-100 text-fuchsia-700': project.status === 'dispute_opened'
                        }">
                            {{
                                project.status === 'open' ? 'Aberto' :
                                    project.status === 'in_progress' ? 'Em andamento' :
                                        project.status === 'completed' ? 'Concluído' :
                                            project.status === 'awaiting_approval' ? 'Aguardando aprovação' :
                                                project.status === 'awaiting_payment' ? 'Aguardando pagamento' :
                                                    project.status === 'payment_approved' ? 'Pagamento aprovado' :
                                                        project.status === 'cancel_requested' ? 'Cancelamento solicitado' :
                                                            project.status === 'dispute_opened' ? 'Disputa aberta' :
                                                                'Cancelado'
                            }}
                        </span>
                    </div>
                </div>

                <!-- DADOS PRINCIPAIS -->
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                    <div class="bg-gray-50 p-4 rounded-xl">
                        <p class="text-sm text-gray-500">Propostas</p>
                        <p class="text-xl font-semibold">{{ project.proposals_count }}</p>
                    </div>

                    <div class="bg-gray-50 p-4 rounded-xl">
                        <p class="text-sm text-gray-500">Prazo final</p>
                        <p class="text-xl font-semibold">{{ formatDate(project.deadline) }}</p>
                    </div>

                    <div class="bg-gray-50 p-4 rounded-xl">
                        <p class="text-sm text-gray-500">Visualizações</p>
                        <p class="text-xl font-semibold">{{ project.views_count }}</p>
                    </div>

                    <!-- NOVO BLOCO: CONTRATANTE -->
                    <div class="bg-gray-50 p-4 rounded-xl">
                        <p class="text-sm text-gray-500">Contratante</p>

                        <router-link v-if="project.creator" :to="{
                            name: 'freelancer',
                            params: {
                                hash: project.creator.hash,
                                reference: project.creator.reference
                            }
                        }" class="font-semibold text-lg text-blue-700 hover:underline">
                            {{ project.creator.name }}
                        </router-link>

                        <span v-else class="text-gray-600">-</span>
                    </div>
                </div>

            </div>

            <!-- DESCRIÇÃO -->
            <div class="bg-white shadow rounded-2xl p-6">
                <h2 class="text-xl font-semibold mb-3">Descrição do projeto</h2>
                <p class="text-gray-700 whitespace-pre-line">{{ project.description }}</p>
            </div>

            <!-- CATEGORIAS (Corrigido para cat.description) -->
            <div v-if="project.categories?.length" class="bg-white shadow rounded-2xl p-6">
                <h2 class="text-xl font-semibold mb-3">Categorias</h2>
                <div class="flex flex-wrap gap-2">
                    <span v-for="(cat, index) in project.categories" :key="index"
                        class="px-3 py-1 bg-gray-800 text-white rounded-full text-sm">
                        {{ cat }} </span>
                </div>
            </div>

            <!-- ANEXOS (Corrigido para file.url e file.name) -->
            <div v-if="project.attachments?.length" class="bg-white shadow rounded-2xl p-6">
                <h2 class="text-xl font-semibold mb-3">Anexos</h2>
                <div class="flex flex-col gap-2">
                    <a v-for="(file, idx) in project.attachments" :key="idx" :href="file.url" target="_blank"
                        class="text-blue-600 underline hover:text-blue-800">
                        <!-- Renderiza o campo 'name' -->
                        📎 {{ file.name }}
                    </a>
                </div>
            </div>

            <!-- ENVIAR PROPOSTA -->
            <div v-if="project.status === 'open' && !isProjectOwner" class="bg-white shadow rounded-2xl p-6">
                <h2 class="text-xl font-semibold mb-4">Enviar proposta</h2>

                <form class="grid grid-cols-1 md:grid-cols-2 gap-4" @submit.prevent="submitProposal">
                    <div>
                        <label class="font-medium">Valor da proposta</label>
                        <input v-model="amountFormatted" @input="formatInput" type="text" class="input-gold" />
                        <div v-if="proposal.amount > 0" class="mt-2 text-sm text-gray-700 space-y-1">
                            <!--<p>
                                Taxa do serviço ({{ FIXED_FEE_PERCENTAGE }}%):
                                <span class="font-semibold text-gray-900">{{ feeAmountDisplay }}</span>
                            </p>-->
                            <p class="text-lg font-bold">
                                Valor total:
                                <span class="text-green-600">{{ totalAmountDisplay }}</span>
                            </p>
                        </div>
                    </div>

                    <div>
                        <label class="font-medium">Dias estimados para entrega</label>
                        <input v-model="proposal.estimated_days" type="number" class="input-gold" />
                    </div>

                    <div class="md:col-span-2">
                        <label class="font-medium">Mensagem</label>
                        <textarea v-model="proposal.message" rows="4" class="input-gold"
                            placeholder="Digite sua proposta..."></textarea>
                    </div>

                    <div class="md:col-span-2 flex justify-end">
                        <button type="submit" :disabled="loading"
                            class="bg-[#b99a52] hover:bg-[#a18446] text-white px-6 py-3 rounded-xl font-semibold shadow hover:bg-[#a18446]">
                            <span v-if="!loading && !proposal.proposalId">Enviar proposta</span>
                            <span v-else-if="loading && !proposal.proposalId">Enviando proposta</span>
                            <span v-else-if="!loading && proposal.proposalId">Atualizar proposta</span>
                            <span v-else>Atualizando proposta</span>
                        </button>
                    </div>
                </form>
                <!-- Caixa de mensagem exibida apenas se o freelancer já enviou proposta -->
                <!--<div v-if="myProposal" class="mt-6 p-4 border border-gray-300 rounded-xl bg-white shadow-sm">

                    <h3 class="font-semibold mb-3 text-gray-800">
                        Enviar mensagem ao contratante
                    </h3>

                    <!-- PREVIEW DOS ANEXOS -->
                <!--     <div v-if="selectedFiles.length" class="bg-gray-100 p-2 rounded-xl border text-sm mb-3">
                        <strong>Anexos selecionados:</strong>
                        <ul class="list-disc ml-5 mt-1">
                            <li v-for="(file, idx) in selectedFiles" :key="idx">
                                {{ file.name }}
                            </li>
                        </ul>
                    </div>

                    <div class="flex gap-2 items-center">
                        <textarea v-model="newMessage" placeholder="Digite sua mensagem..." class="input-gold flex-1"
                            rows="3">
                        </textarea>

                        <!-- BOTÃO UPLOAD -->
                <!--        <label class="cursor-pointer bg-gray-300 px-3 py-2 rounded-xl text-sm font-semibold">
                            📎
                            <input type="file" multiple class="hidden" @change="handleFileSelect" />
                        </label>

                        <button @click="sendMessage(myProposal)" :disabled="loadingMessage"
                            class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                            <span v-if="!loadingMessage">Enviar</span>
                            <span v-else>Enviando...</span>
                        </button>
                    </div>

                </div>-->
            </div>

            <div v-if="canCancelProject || canViewDispute || showDisputeSubmittedMsg || (project.status === 'in_progress' && myProposal?.status === 'accepted')"
                class="bg-white shadow rounded-2xl p-6">
                <div class="flex justify-between items-center">

                    <!-- BOTÃO CANCELAR -->
                    <button v-if="canCancelProject" @click="openCancelModal()"
                        :disabled="loadingCancel || !canClickCancelConfirm"
                        class="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold shadow disabled:opacity-60 disabled:cursor-not-allowed">
                        <span v-if="!loadingCancel">{{ cancelActionLabel }}</span>
                        <span v-else>Processando...</span>
                    </button>

                    <button v-if="canViewDispute" @click="router.push(`/disputas/${project.dispute_id}`)"
                        :disabled="loadingDisputeStatus"
                        class="bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-6 py-3 rounded-xl font-semibold shadow disabled:opacity-60 disabled:cursor-not-allowed">
                        <span v-if="!loadingDisputeStatus">Ir para disputa</span>
                        <span v-else>Carregando…</span>
                    </button>

                    <div v-else-if="showDisputeSubmittedMsg"
                        class="px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 font-semibold">
                        Você já enviou sua proposta na disputa
                    </div>

                    <!-- BOTÃO CONCLUIR (somente freelancer com proposal accepted) -->
                    <button v-if="myProposal?.status === 'accepted' && project.status !== 'dispute_opened'"
                        @click="openConclusionModal(myProposal)" :disabled="loadingConclusion"
                        class="bg-[#b99a52] hover:bg-[#a18446] text-white px-6 py-3 rounded-xl font-semibold shadow disabled:opacity-60 disabled:cursor-not-allowed">
                        <span v-if="!loadingConclusion">Concluir projeto</span>
                        <span v-else>Concluindo...</span>
                    </button>

                </div>
            </div>

            <!-- AVALIAÇÃO DO CONTRATANTE -->
            <UserReview v-if="canReviewProject && project.accepted_proposal_id === myProposal.id"
                :project-id="project.id" :reviewed-id="project.created_by" :reviewer-id="userData.user.id"
                :review-type="contractor" :expires-at="project.review_expires_at" />

        </div>

        <!-- MODAL: CONCLUIR PROJETO -->
        <div v-if="showConclusionModal"
            class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6 z-[9999]">
            <div class="bg-white w-full max-w-lg p-6 rounded-2xl shadow-xl">

                <h2 class="text-2xl font-bold mb-4 text-center">
                    Concluir Projeto
                </h2>

                <p class="text-gray-700 text-center mb-6">
                    Tem certeza que deseja marcar este projeto como concluído?
                </p>

                <div class="flex gap-3">
                    <button @click="closeConclusionModal" class="flex-1 bg-gray-400 text-white px-4 py-2 rounded-xl">
                        Voltar
                    </button>

                    <button @click="confirmConclusion" :disabled="loadingConclusion"
                        class="flex-1 bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700">
                        <span v-if="!loadingConclusion">Confirmar</span>
                        <span v-else>Confirmando...</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- MODAL: CANCELAR PROPOSTA -->
        <div v-if="showCancelModal"
            class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6 z-[9999]">
            <div class="bg-white w-full max-w-lg p-6 rounded-2xl shadow-xl">

                <h2 class="text-2xl font-bold mb-4 text-center">
                    {{ cancelActionLabel }}
                </h2>

                <p class="text-gray-700 text-center mb-6">
                    {{ cancelActionDescription }}
                </p>

                <div class="flex gap-3">
                    <button @click="closeCancelModal" class="flex-1 bg-gray-400 text-white px-4 py-2 rounded-xl">
                        Voltar
                    </button>

                    <button @click="confirmCancelProject" :disabled="loadingCancel || !canClickCancelConfirm"
                        class="flex-1 bg-red-600 text-white px-4 py-2 rounded-xl hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed">
                        <span v-if="!loadingCancel">{{ cancelActionLabel }}</span>
                        <span v-else>Processando...</span>
                    </button>
                </div>
            </div>
        </div>

        <AppFooter />
    </div>

</template>

<script setup>
import { ref, onMounted, getCurrentInstance, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import UserReview from '@/components/UserReview.vue'
import api from '@/services/api';
import { useLoadingStore } from '@/store/loadingStore';

const loadingStore = useLoadingStore();
const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const amountFormatted = ref('');
const loading = ref(false);
const loadingMessage = ref(false);
const loadingConclusion = ref(false);
const loadingCancel = ref(false);
const project = ref({});
const proposal = ref({
    amount: "",
    estimated_days: "",
    message: "",
    proposalId: null,
});
const userData = JSON.parse(localStorage.getItem('userData') || '{}')

const selectedFiles = ref([]);

const myProposal = ref(null);
const newMessage = ref("");

const showConclusionModal = ref(false)
const showCancelModal = ref(false)

const proposalToConclude = ref(null)
const FIXED_FEE_PERCENTAGE = 7.00;
const totalAmountDisplay = ref('');
const feeAmountDisplay = ref('');


const disputeSubmittedByMe = ref(false)
const loadingDisputeStatus = ref(false)
const disputeChecked = ref(false)
const canAccessDispute = ref(false)

function calculateTotalAmount(baseAmount) {
    if (baseAmount === null || baseAmount <= 0) {
        totalAmountDisplay.value = '';
        feeAmountDisplay.value = '';
        return;
    }

    const fee = baseAmount * (FIXED_FEE_PERCENTAGE / 100);

    const total = baseAmount + fee;

    totalAmountDisplay.value = new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(total);

    feeAmountDisplay.value = new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(fee);

    proposal.value.total_amount = total.toFixed(2);
    proposal.value.fee_percentage = FIXED_FEE_PERCENTAGE.toFixed(2);
}

function handleFileSelect(event) {
    selectedFiles.value = Array.from(event.target.files);
}

const canReviewProject = computed(() => {
    if (!project.value) return false
    if (!myProposal.value) return false

    // projeto precisa estar concluído
    if (project.value.status !== 'completed') return false

    // proposta precisa estar finalizada
    if (myProposal.value.status !== 'finished') return false

    if (project.value.freelancer_reviewed_contractor) return false

    // precisa existir data de expiração
    if (!project.value.review_expires_at) return false

    const now = new Date()
    const expiresAt = new Date(project.value.review_expires_at)

    return now <= expiresAt
})

const isLoggedIn = computed(() => !!userData?.user?.id)

const isProjectOwner = computed(() => {
    const userId = userData?.user?.id
    const ownerId = project.value?.created_by
    if (!userId || !ownerId) return false
    return Number(userId) === Number(ownerId)
})

const isFreelancerOnProject = computed(() => {
    const userId = userData?.user?.id
    const assignedTo = project.value?.assigned_to
    if (!userId || !assignedTo) return false
    return Number(userId) === Number(assignedTo)
})

const canCancelProject = computed(() => {
  if (!isLoggedIn.value) return false
  const status = project.value?.status
  if (!status) return false

  const allowed = isProjectOwner.value || isFreelancerOnProject.value
  if (!allowed) return false

  const cancellableStatuses = [
    'in_progress',
    'awaiting_approval',
    'awaiting_payment',
    'payment_approved',
    'cancel_requested'
  ]

  if (!project.value?.assigned_to) return false

  return cancellableStatuses.includes(status)
})

const cancelActionLabel = computed(() => {
    if (!project.value?.status) return 'Cancelar projeto'

    // caso pago com pedido pendente
    if (project.value.status === 'cancel_requested') {
        const requestedBy = project.value.cancel_requested_by
        const myId = userData?.user?.id

        if (requestedBy && myId && Number(requestedBy) === Number(myId)) {
            return 'Aguardando confirmação'
        }
        return 'Confirmar cancelamento'
    }

    return 'Cancelar projeto'
})

const cancelActionDescription = computed(() => {
    if (!project.value?.status) return 'Tem certeza que deseja cancelar o projeto?'

    if (project.value.status === 'cancel_requested') {
        const requestedBy = project.value.cancel_requested_by
        const myId = userData?.user?.id

        if (requestedBy && myId && Number(requestedBy) === Number(myId)) {
            return 'Você já solicitou o cancelamento. Aguardando a outra parte confirmar.'
        }
        return 'A outra parte solicitou o cancelamento. Ao confirmar, o cancelamento será efetivado.'
    }

    if (project.value.status === 'payment_approved') {
        return 'O pagamento já foi aprovado. Você irá solicitar o cancelamento e a outra parte precisará confirmar.'
    }

    return 'Tem certeza que deseja cancelar o projeto? Essa ação é irreversível.'
})

const canClickCancelConfirm = computed(() => {
    // se eu mesmo pedi, não deixo clicar pra "confirmar" de novo
    if (project.value?.status !== 'cancel_requested') return true

    const requestedBy = project.value.cancel_requested_by
    const myId = userData?.user?.id
    if (!requestedBy || !myId) return true

    return Number(requestedBy) !== Number(myId)
})

const canViewDispute = computed(() => {
    return project.value?.status === 'dispute_opened'
        && !!project.value?.dispute_id
        && disputeChecked.value            // só depois de checar
        && canAccessDispute.value          // só se tiver permissão
        && !disputeSubmittedByMe.value     // só se ainda não enviou
})

const showDisputeSubmittedMsg = computed(() => {
    return project.value?.status === 'dispute_opened'
        && !!project.value?.dispute_id
        && disputeChecked.value
        && canAccessDispute.value
        && disputeSubmittedByMe.value
})

function formatDate(dt) {
    if (!dt) return "-";
    return new Date(dt).toLocaleDateString("pt-BR");
}

function urgencyLabel(level) {
    return {
        low: "Baixa urgência",
        medium: "Urgência média",
        high: "Alta urgência"
    }[level] || level;
}

function processAttachments(paths) {
    if (!paths || !Array.isArray(paths)) return [];

    const base = process.env.VUE_APP_URL_API_STORAGE || "";

    const baseClean = base.replace(/\/+$/, "");

    return paths.map(p => {
        if (!p) return null;

        const maybeUrl = String(p);
        const isFullUrl = /^(https?:)?\/\//i.test(maybeUrl);

        const normalizedPath = maybeUrl.replace(/\\/g, "/").replace(/^\/+/, "");

        let url;
        if (isFullUrl) {
            url = maybeUrl;
        } else if (baseClean) {
            url = `${baseClean}/${normalizedPath}`;
        } else {
            url = `/${normalizedPath}`;
        }

        url = encodeURI(url);

        const nameMatch = normalizedPath.match(/[^\/]+$/);
        const name = nameMatch ? nameMatch[0] : "Arquivo";

        return { url, name };
    }).filter(Boolean);
}

onMounted(async () => {
    await getProject();

    if (project.value?.status === 'dispute_opened' && project.value?.dispute_id) {
        await fetchMyDisputeStatus();
    } else {
        disputeSubmittedByMe.value = false;
        canAccessDispute.value = false;
        disputeChecked.value = false;
    }

    if (userData) {
        await getProposal();
    }
});

async function submitProposal() {
    try {
        if (!userData) {
            const currentRoute = router.currentRoute.value.fullPath;

            proxy.$swal.fire({
                icon: 'info',
                title: 'É necessário fazer login',
                text: 'Você precisa estar logado para enviar uma proposta.'
            }).then(() => {
                router.replace({
                    path: '/cadastro',
                    query: { redirect: currentRoute }
                });
            });
            return;
        }

        if (proposal.value.amount <= 0) {
            proxy.$swal.fire({
                icon: "warning",
                title: "Valor inválido",
                text: "O valor da proposta deve ser maior que zero."
            });
            return;
        }

        /*if (proposal.value.amount < 30) {
            proxy.$swal.fire({
                icon: "warning",
                title: "Valor muito baixo",
                text: "O valor da proposta mínimo é R$ 30,00."
            });
            return;
        }*/

        loading.value = true;
        const days = parseInt(proposal.value.estimated_days);
        if (isNaN(days) || days <= 0) {
            proxy.$swal.fire({ icon: 'warning', title: 'Prazo de entrega obrigatório', text: 'Informe um número válido de dias estimados para entrega.' });
            return;
        }

        if (!proposal.value.message.trim()) {
            proxy.$swal.fire({ icon: 'warning', title: 'Mensagem obrigatória', text: 'Inclua uma mensagem detalhada na sua proposta.' });
            return;
        }
        const hash = project.value.hash;
        const slug = project.value.slug;

        if (!hash || !slug) {
            proxy.$swal.fire({
                icon: "error",
                title: "Erro",
                text: "Referência do projeto não encontrada."
            });
            return;
        }

        proposal.value.hash = hash;
        proposal.value.slug = slug;
        const payload = {
            ...proposal.value,
            hash,
            slug
        };

        const isUpdate = !!proposal.value.proposalId;
        let response;
        let successMessage;

        if (isUpdate) {
            response = await api.put(`/proposal/${hash}/${slug}/proposal`, payload);
            successMessage = response.data?.message || "Proposta atualizada com sucesso!";
        } else {
            response = await api.post(`/proposal/${hash}/${slug}/proposal`, payload);
            successMessage = response.data?.message || "Proposta enviada com sucesso!";

            await getProposal();
        }

        proxy.$swal.fire({
            icon: "success",
            title: "",
            text: successMessage
        });

        await getProject();
        await getProposal();
    } catch (error) {
        console.error("Erro ao enviar proposta", error);

        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao enviar proposta."
        });
    } finally {
        loading.value = false;
    }
}

async function getProject() {
    try {
        loadingStore.show();
        const hash = route.params.hash;
        const slug = route.params.slug;

        const response = await api.get(`/projects/hash/${hash}/${slug}`);

        let projectData = response.data.data;

        if (projectData) {
            projectData.attachments = processAttachments(projectData.attachments);

            project.value = projectData;
        } else {
            throw new Error("Dados do projeto inválidos ou não encontrados.");
        }

    } catch (error) {
        console.error("Erro ao carregar projeto:", error);

        proxy.$swal.fire({
            icon: "error",
            title: "Erro",
            text: error?.response?.data?.error || "Erro ao consultar projeto."
        });
    } finally {
        loadingStore.hide();
    }
}

async function getProposal() {
    try {
        loadingStore.show();

        const hash = route.params.hash;
        const slug = route.params.slug;

        const response = await api.get(`/proposal/hash/${hash}/${slug}`);
        const data = response.data.data;

        if (data) {
            proposal.value.proposalId = data.id;

            proposal.value.amount = Number(data.amount);

            proposal.value.estimated_days = data.estimated_days || "";
            proposal.value.message = "";

            amountFormatted.value = new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(proposal.value.amount);

            totalAmountDisplay.value = new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(Number(data.total_amount));

            feeAmountDisplay.value = new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(
                Number(data.total_amount) - Number(data.amount)
            );

            myProposal.value = data;
        }
    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "Erro",
            text: "Erro ao consultar proposta."
        });
    } finally {
        loadingStore.hide();
    }
}

async function fetchMyDisputeStatus() {
    if (!project.value?.dispute_id) return;

    loadingDisputeStatus.value = true;
    disputeChecked.value = false;

    try {
        const res = await api.get(`/disputes/${project.value.dispute_id}/me`);

        canAccessDispute.value = true;
        disputeSubmittedByMe.value = !!res.data?.data?.submitted;

    } catch (e) {
        const status = e?.response?.status;

        if (status === 403 || status === 401) {
            canAccessDispute.value = false;
            disputeSubmittedByMe.value = false;
            return;
        }

        canAccessDispute.value = false;
        disputeSubmittedByMe.value = false;

    } finally {
        disputeChecked.value = true;
        loadingDisputeStatus.value = false;
    }
}

async function sendMessage(proposal) {
    if (!newMessage.value.trim() && selectedFiles.value.length === 0) return;

    try {
        loadingStore.show();
        loadingMessage.value = true;

        const formData = new FormData();
        formData.append("project_id", proposal.project_id);
        formData.append("receiver_id", project.value.created_by);
        formData.append("content", newMessage.value);

        selectedFiles.value.forEach(file => {
            formData.append("attachments[]", file);
        });

        await api.post("/messages/send", formData, {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });

        // Limpar campos
        newMessage.value = "";
        selectedFiles.value = [];

        proxy.$swal.fire({
            icon: "success",
            title: "",
            text: "Mensagem enviada com sucesso."
        });

    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao enviar mensagem."
        });
    } finally {
        loadingStore.hide();
        loadingMessage.value = false;
    }
}

function openCancelModal() {
    showCancelModal.value = true
}

function closeCancelModal() {
    showCancelModal.value = false
}

function openConclusionModal(proposal) {
    proposalToConclude.value = proposal
    showConclusionModal.value = true
}

function closeConclusionModal() {
    showConclusionModal.value = false
}

async function confirmConclusion() {
    if (!proposalToConclude.value) return

    try {
        loadingConclusion.value = true

        await api.post(`/projects/${project.value.id}/conclude`, {
            proposal_id: proposalToConclude.value.id
        })

        closeConclusionModal()
        await getProject()

        proxy.$swal.fire({
            icon: "success",
            title: "",
            text: "Projeto concluído com sucesso!"
        })
    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao concluir projeto."
        })
    } finally {
        loadingConclusion.value = false
    }
}

async function confirmCancelProject() {
    try {
        loadingCancel.value = true

        const res = await api.post(`/projects/${project.value.id}/cancel`)

        proxy.$swal.fire({
            icon: "success",
            title: "",
            text: res.data?.message || "Ação realizada com sucesso.",
            customClass: { container: 'swal-top-layer' }
        })

        closeCancelModal()

        if (res.data?.dispute_id) {
            router.push(`/disputas/${res.data.dispute_id}`)
            return
        }

        await getProject()
        if (!isProjectOwner.value) {
            await getProposal()
        }

    } catch (error) {
        proxy.$swal.fire({
            icon: "error",
            title: "",
            text: error?.response?.data?.error || "Erro ao cancelar proposta.",
            customClass: {
                container: 'swal-top-layer'
            }
        })
    } finally {
        loadingCancel.value = false
    }
}

const formatInput = (event) => {
    const clean = event.target.value.replace(/\D/g, "");
    const num = parseInt(clean, 10) / 100;

    proposal.value.amount = isNaN(num) ? null : num;

    amountFormatted.value = proposal.value.amount
        ? new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(proposal.value.amount)
        : "";
    calculateTotalAmount(proposal.value.amount);
};

watch(
    () => proposal.value.amount,
    (newValue) => {
        if (newValue !== null && typeof newValue === 'number') {
            amountFormatted.value = new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(newValue);

            calculateTotalAmount(newValue);
        } else {
            totalAmountDisplay.value = '';
            feeAmountDisplay.value = '';
            proposal.value.total_amount = null;
            proposal.value.fee_percentage = null;
        }
    }, { immediate: true }
);


</script>

<style>
.swal-top-layer {
    z-index: 99999 !important;
}
</style>